import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import i18next from 'i18next';
import React from 'react';

import styles from './Dialog.module.scss';
import { RadioButtonGroup } from '../ui-kit/RadioButtonGroup';
import { CheckboxGroup } from '../ui-kit/CheckboxGroup';
import type { useModal } from '../ui-kit/modal/useModal';
import { ConfirmOptions } from '../ui-kit/modal/useModal.types';

export async function showSelectOptionModal(
  modals: ReturnType<typeof useModal>,
  options: ConfirmOptions & {
    title: string;
    options: string[];
  },
): Promise<string | null> {
  const result = await modals.showCustom<{
    selectedFieldValue?: number;
    customTextValue?: string;
    nameTextError?: string;
  }>({
    defaultValue: {
      nameTextError: '',
    },
    validate: (value) => {
      if (!value) return false;

      if (
        (!value?.customTextValue?.trim() &&
          customOptionIntValue === value?.selectedFieldValue) ||
        (!value?.selectedFieldValue && value?.selectedFieldValue !== 0)
      ) {
        value.nameTextError = i18next.t('errors.required');
        return false;
      }

      return true;
    },
    ...options,
    cancelButtonColor: ButtonColor.Primary,
    Component: (props) => {
      return (
        <div>
          <div className={styles.text} data-test-id="dialog-text">
            {options.text}
          </div>
          <RadioButtonGroup<Option, 'id'>
            rootClassName={styles.options}
            radioButtonClassName={styles.radioButton}
            inputClassName={styles.inputOption}
            options={options.options
              .map((value, index) => {
                return {
                  id: index,
                  value: value,
                };
              })
              .concat({
                id: customOptionIntValue,
                value: props.value?.customTextValue ?? '',
              })}
            valueField={'id'}
            labelField={'value'}
            editableFieldValue={customOptionIntValue}
            onEditableFieldChange={(value) =>
              props.setValue({
                selectedFieldValue: props.value?.selectedFieldValue ?? 0,
                customTextValue: value ?? '',
                nameTextError: undefined,
              })
            }
            value={props.value?.selectedFieldValue ?? undefined}
            onSelectedOptionChanged={(option) =>
              props.setValue({
                selectedFieldValue: option?.id ?? 0,
                customTextValue: props.value?.customTextValue ?? '',
                nameTextError: undefined,
              })
            }
            required={true}
            error={!!props.value?.nameTextError}
            errorText={props.value?.nameTextError}
          />
        </div>
      );
    },
  });
  if (result === null) return null;
  if (result.selectedFieldValue === customOptionIntValue)
    return result.customTextValue!;
  return options.options[result.selectedFieldValue!];
}

export async function showSelectOptionsModal(
  modals: ReturnType<typeof useModal>,
  options: ConfirmOptions & {
    title: string;
    options: Option[];
    selectedOptions?: Option[];
  },
): Promise<Option[] | undefined> {
  const result = await modals.showCustom<{
    selectedOptions: Option[];
  }>({
    ...options,
    defaultValue: { selectedOptions: options.selectedOptions ?? [] },
    cancelButtonColor: ButtonColor.Primary,
    Component: (props) => {
      return (
        <div>
          <div className={styles.text} data-test-id="dialog-text">
            {options.text}
          </div>
          <CheckboxGroup<Option, 'id'>
            rootClassName={styles.options}
            options={options.options}
            valueField={'id'}
            labelField={'value'}
            // editableFieldValue={customOptionIntValue}
            // onEditableFieldChange={setInputValue}
            selectedValues={(props.value?.selectedOptions ?? []).map(
              (option) => option.id,
            )}
            onSelectedOptionChanged={(options) =>
              props.setValue({ selectedOptions: options })
            }
          />
        </div>
      );
    },
  });
  return result?.selectedOptions;
}

export interface Option {
  id: number;
  value: string;
}

const customOptionIntValue = -2;
